import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {TranslocoService} from '@jsverse/transloco';
import {LocalStorageService} from '../localstorage/local-storage.service';

export type SupportedLanguageCode = 'fr' | 'en';

export interface SupportedLanguage {
  code: SupportedLanguageCode;
  labelKey: string;
}

const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: 'fr', labelKey: 'language.name.fr' },
  { code: 'en', labelKey: 'language.name.en' }
];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  constructor(
    private readonly transloco: TranslocoService,
    private readonly localStorage: LocalStorageService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  public getSupportedLanguages(): SupportedLanguage[] {
    return SUPPORTED_LANGUAGES;
  }

  public getActiveLanguage(): SupportedLanguageCode {
    return this.normalizeLanguage(this.transloco.getActiveLang()) ?? 'en';
  }

  public setActiveLanguage(language: string): SupportedLanguageCode | null {
    const normalized = this.normalizeLanguage(language);
    if (!normalized) {
      return null;
    }

    this.transloco.setActiveLang(normalized);

    if (isPlatformBrowser(this.platformId)) {
      this.setCookie('lang', normalized, 365);
      this.document.documentElement.lang = normalized;
    }

    return normalized;
  }

  getWebsiteLanguage(): string {
    // 1) Transloco active lang
    const active = this.normalizeLanguage(this.transloco.getActiveLang());
    if (active) return active;

    // 2) <html lang> (set by AppComponent)
    if (isPlatformBrowser(this.platformId)) {
      const docLang = this.normalizeLanguage(this.document.documentElement.lang);
      if (docLang) return docLang;

      // 3) navigator fallback
      const nav = this.normalizeLanguage(navigator.language);
      if (nav) return nav;
    }

    // 4) default
    return 'en';
  }

  public persistPreferredLanguage(language: string): void {
    const normalized = this.normalizeLanguage(language);
    if (!normalized) {
      return;
    }
    this.localStorage.save('preferredLanguage', normalized);
  }

  public getPersistedPreferredLanguage(): SupportedLanguageCode | null {
    return this.normalizeLanguage(this.localStorage.get('preferredLanguage'));
  }

  public clearPersistedPreferredLanguage(): void {
    this.localStorage.remove('preferredLanguage');
  }

  private normalizeLanguage(language: string | null | undefined): SupportedLanguageCode | null {
    if (!language) {
      return null;
    }

    const normalized = language.toLowerCase().replace('_', '-');
    if (normalized.startsWith('fr')) {
      return 'fr';
    }
    if (normalized.startsWith('en')) {
      return 'en';
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    this.document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;
  }
}
